/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1677024000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Feb 22 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "gladly",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Gladly",
          "depth": 0,
          "id": "connect-to-gladly"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Customers",
          "depth": 1,
          "id": "customers"
        }, {
          "value": "Tasks",
          "depth": 1,
          "id": "tasks"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      img: "img",
      code: "code",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Gladly is a customer service platform that enables businesses to communicate with their customers across various channels, such as phone, email, chat, social media, and more. Syncing data to Gladly means integrating your customer data with the Gladly platform so that your customer service team can access and use this data to provide better and more personalized support to your customers."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customers"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Gladly customers"
            }), _jsx(_components.td, {
              children: "Insert, Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.gladly.com/rest/#tag/Customers",
                children: "Customer docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Tasks"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to any Gladly tasks"
            }), _jsx(_components.td, {
              children: "Insert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developer.gladly.com/rest/#tag/Tasks",
                children: "Task docs"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-gladly",
        children: "Connect to Gladly"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Gladly"
        }), ". You can then authenticate Hightouch to Gladly by entering:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "A Gladly API Token"
        }), "\n", _jsx(_components.li, {
          children: "Email on your Gladly account"
        }), "\n", _jsx(_components.li, {
          children: "Gladly domain"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To retrieve a Gladly ", _jsx(_components.strong, {
          children: "API Token"
        }), ", open the hamburger menu in the top left corner of Gladly. Select the ", _jsx(_components.strong, {
          children: "Settings"
        }), " option. Under the ", _jsx(_components.strong, {
          children: "App Developer Tools"
        }), " section, click the ", _jsx(_components.strong, {
          children: "API Tokens"
        }), " option. You can use an existing token or click the ", _jsx(_components.strong, {
          children: "Create Token"
        }), " button to generate a new one."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destinations-gladly-api-token.png",
          alt: "Finding Gladly API Token"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["We recommend having the following roles on the account: ", _jsx(_components.code, {
          children: "Administrator"
        }), ", ", _jsx(_components.code, {
          children: "Agent"
        }), ", ", _jsx(_components.code, {
          children: "API User"
        }), ", and ", _jsx(_components.code, {
          children: "Developer"
        }), ".\nTo check the roles, you select ", _jsx(_components.strong, {
          children: "Users"
        }), " under the ", _jsx(_components.strong, {
          children: "People"
        }), " section. Hover over the row with the ", _jsx(_components.strong, {
          children: "Email"
        }), " you provided to Hightouch. Click on the icon with three dots that appear and select ", _jsx(_components.strong, {
          children: "Edit"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destinations-gladly-account-roles.png",
          alt: "Finding Gladly Account Roles"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To get the ", _jsx(_components.strong, {
          children: "Email"
        }), " on your Gladly account, click the user icon in the top right corner. Select the ", _jsx(_components.strong, {
          children: "Profile"
        }), " option. You should see the account's associated ", _jsx(_components.strong, {
          children: "Email"
        }), " there."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destinations-gladly-email.png",
          alt: "Finding Gladly Email"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Domain"
        }), " is the Gladly domain you are logged into. It should be similar to ", _jsx(_components.code, {
          children: "https://{organization}.gladly.com"
        }), ". You only need to provide this portion: ", _jsx(_components.code, {
          children: "{organization}.gladly.com"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've connected your Gladly destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Gladly destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "customers",
        children: "Customers"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports inserting, upserting and updating customers."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows in your model with customers in Gladly on ", _jsx(_components.strong, {
          children: "Email"
        }), ", ", _jsx(_components.strong, {
          children: "External Customer ID"
        }), ", or ", _jsx(_components.strong, {
          children: "Phone Number"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "field-and-custom-mappings",
        children: "Field and custom mappings"
      }), "\n", _jsx(_components.p, {
        children: "You can sync any columns from your source to both default properties or custom attributes."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Make sure that if you are creating a new custom attribute, please inform ", _jsx(_components.a, {
            href: "https://help.gladly.com/docs/contact-gladly-support",
            children: "Gladly Customer Support"
          }), " before or after running the sync in order for this attribute to be seen/used on Gladly. If the custom attribute already exists, then you are all set."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "tasks",
        children: "Tasks"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports creating new tasks and updating existing ones."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows in your model with tasks in Gladly on ", _jsx(_components.strong, {
          children: "Task ID"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync any columns from your source to default properties in Gladly."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
